// @ts-nocheck
/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { screenWidthWithoutSidebarState, timezoneState } from '../../states';
import { useTraceCtx } from '../../components/trace/TraceContext';
import { Dayjs } from 'dayjs';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import {
  dayjs,
  ET,
  getCurrentDate,
  getDateFormatted,
  getDefaultSlotProps,
  getQueryDate,
  marketDayAdd,
  isLatestTradingDay,
  isMarketOpenOnDate,
} from '../../util';
import { getApiBarType } from '../../util/oi';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  TraceLense,
  TraceStrikeBarType,
  OIEntity,
  OIScaleRange,
  PRICE_BOUNDS,
  STRIKE_ZOOM_BOUNDS,
  ZoomData,
} from '../../types';
import { SGTooltip } from '../../components/core';
import SettingsPopout from '../../components/shared/SettingsPopout';
import { InfoButton, SGSelect } from '../../components';
import {
  HEATMAP_FIRST_AVAILABLE_DATE,
  TRACE_BAR_TITLES,
  TraceFiltersReadable,
  TraceShowChartType,
  LAST_TRACE_UPDATE_HOUR_ET,
  TRACE_LENSES,
} from '../../config/oi';
import { ComboSymbol, StrikeBarTypeLabels } from '../../config';
import { ChartSizingButton } from '../../components/shared/ChartSizingButton';
import FullscreenExitSharpIcon from '@mui/icons-material/FullscreenExitSharp';
import FullscreenSharpIcon from '@mui/icons-material/FullscreenSharp';
import { HideSupportStyle } from '../../components/shared/HideSupportStyle';
import { TraceVolGauge } from './TraceVolGauge';
import { ColorMode } from '../../theme';
import { TraceColorSettings } from '../../components/trace/TraceColorSettings';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

type TraceControlsProps = {
  timestamps: dayjs.Dayjs[] | number[];
  zoomData: ZoomData | undefined;
  resetZoom: () => void;
  setStrikeBarsLoading: (loading: boolean) => void;
  timestamp: dayjs.Dayjs | number | null;
  statsData: any;
  gammaAtLastPriceUninverted: number | undefined;
  lastUpdateCheckAt: dayjs.Dayjs | null;
};

const IntradayFiltersTooltip = new Map([
  [TraceLense.GAMMA, 'Shows gamma values on the chart'],
  [TraceLense.DELTA, 'Shows delta values on the chart'],
  [
    TraceLense.DELTA_DIRECTIONAL,
    `Shows the direction of delta at the current price per timestamp and strike`,
  ],
  [
    TraceLense.GAMMA_DIRECTIONAL,
    `Shows the direction of gamma at the current price per timestamp and strike`,
  ],
  [TraceLense.GAMMA_COLOR, `Shows the change in gamma per minute`],
  [
    TraceLense.DELTA_CHARM_DIRECTIONAL,
    `Shows the change in delta buying/selling pressure per minute`,
  ],
  [
    TraceLense.DELTA_END_DIFF,
    `Shows the change in delta between close and spot/time`,
  ],
]);

const StrikeBarTypeTooltips = new Map([
  [TraceStrikeBarType.GAMMA, `Show Gamma Exposure in the bar chart`],
  [TraceStrikeBarType.OI, 'Show Open Interest in the bar chart'],
  [TraceStrikeBarType.OI_NET, 'Show Net Open Interest in the bar chart'],
  [TraceStrikeBarType.NONE, 'Hide the strike bar chart'],
]);

const OIENTITY_READABLE_ARRAY = [
  [OIEntity.MARKET_MAKER, 'Market Makers'],
  [OIEntity.CUST, 'Customers'],
  [OIEntity.PROCUST, 'Pro Customers'],
  [OIEntity.BD, 'Broker Dealers'],
  [OIEntity.FIRM, 'Firms'],
];
const OIENTITY_READABLE = new Map<OIEntity, string>(OIENTITY_READABLE_ARRAY);

const OIENTITY_TOOLTIP = new Map<OIEntity, string>([
  [
    OIEntity.MARKET_MAKER,
    'These entities provide liquidity by taking the other side of options trades and hedging dynamically. This hedging creates significant underlying price impact. Most TRACE users benefit from keeping Market Makers as their selected lens.',
  ],
  [
    OIEntity.CUST,
    'Retail traders, hedge funds, and asset managers taking directional or volatility views. Their trades are often on the opposite side of market makers, thus causing dealer hedging.',
  ],
  [
    OIEntity.PROCUST,
    'High-volume and sophisticated traders, often prop traders.',
  ],
  [
    OIEntity.FIRM,
    'Trading desks of brokerages who trade for their own accounts (professional participants in options markets).',
  ],
  [
    OIEntity.BD,
    'Registered intermediaries executing trades for clients or themselves. This may include firms, internal desks, or structured product desks.',
  ],
]);

export const TraceControls = ({
  timestamps,
  zoomData,
  resetZoom,
  setStrikeBarsLoading,
  timestamp,
  gammaAtLastPriceUninverted,
  statsData,
  lastUpdateCheckAt,
}: TraceControlsProps) => {
  const {
    config,
    updateConfig,
    userTraceV2Enabled,
    showInternalLenses,
    isMobile,
    mode,
    intradayDate,
    intradaySym,
    selectedLense,
    fullscreen,
    setFullscreen,
    showChartType,
    setShowChartType,
  } = useTraceCtx();

  const filterPrice = config.filterLinesBasedOnTimeSlider;
  const setFilterPrice = (v: boolean) =>
    updateConfig({ filterLinesBasedOnTimeSlider: v });
  const parquetKeys = [config.mktActor];
  const setParquetKeys = (v: OIEntity[]) =>
    updateConfig({ mktActor: v[0] ?? OIEntity.MARKET_MAKER });
  const isInverted = config.inverted;
  const setIsInverted = (v: boolean) => updateConfig({ inverted: v });

  const theme = useTheme();
  const defaultSlotProps = getDefaultSlotProps(theme);
  const queryDate = getQueryDate(true);
  const afterProjectionEODUpdates =
    queryDate >= queryDate.hour(17).minute(30).second(0).millisecond(0);
  const queryDateBodTs = queryDate.startOf('day').valueOf();
  const maxQueryDate = useMemo(
    () =>
      userTraceV2Enabled
        ? marketDayAdd(queryDate, afterProjectionEODUpdates ? 6 : 5)
        : dayjs(getQueryDate(true).format()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryDateBodTs, afterProjectionEODUpdates, userTraceV2Enabled],
  );

  const tz = useRecoilValue(timezoneState);

  const priceBounds = config.priceZoom;
  const strikeZoomBounds = config.strikeZoom;
  const candleDuration = config.candleDuration;
  const showKeyLevels = config.showKeyLevels;
  const scaleRange = config.scaleRange;
  const showGexZeroDte = config.showGexZeroDte;
  const hiroSymsMap = config.hiroSyms;
  const showColorScale = config.showColorScale;
  const showContourLines = config.showContourLines;
  const showPriceLine = config.showPriceLine;
  const statsLookbackDays = config.statsLookbackDays;
  const useWhiteMode = config.useWhiteMode;
  const trackerEnabled = config.strikeBarsTrackerEnabled;
  const historyEnabled = config.strikeBarsHistoryEnabled;
  const strikeBarType = config.strikeBarType;
  const screenWidthWithoutSidebar = useRecoilValue(
    screenWidthWithoutSidebarState,
  );

  const hiroSym = hiroSymsMap?.[intradaySym] ?? intradaySym;

  const handleStrikeBarTypeChange = (newType: TraceStrikeBarType) => {
    const oldApi = getApiBarType(strikeBarType);
    const newApi = getApiBarType(newType);
    if (newApi !== oldApi && newType !== TraceStrikeBarType.NONE) {
      setStrikeBarsLoading(true);
    }
    updateConfig({ strikeBarType: newType });
  };
  const replaceTitleWithTools = !isMobile && screenWidthWithoutSidebar <= 835;

  const horizGap = isMobile
    ? '5px'
    : screenWidthWithoutSidebar > 1_000
      ? '20px'
      : '10px';

  let selectedTimestampIndex = timestamps.length - 1;
  for (let i = timestamps.length - 1; i >= 0; i--) {
    if (timestamp?.isSame(timestamps[i], 'second')) {
      selectedTimestampIndex = i;
      break;
    }
  }

  const isLatestTimestamp = selectedTimestampIndex === timestamps.length - 1;
  let suppressStability = false;
  if (selectedTimestampIndex > timestamps.length / 2 && timestamp != null) {
    const etTime = timestamp.tz(ET);
    suppressStability =
      (etTime.hour() === 15 && etTime.minute() >= 30) || etTime.hour() >= 16;
  }

  const datePicker = (
    <SGTooltip
      title="View TRACE data for historical dates. Note that HIRO values are only available for 5 days"
      placement="left"
    >
      <Box
        sx={{ height: '40px', alignSelf: 'center' }}
        className="canvas-no-drag"
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            views={['day']}
            value={intradayDate}
            onChange={(newValue: Dayjs | null) => {
              if (newValue && newValue.isValid()) {
                updateConfig({ date: getDateFormatted(newValue) });
              }
            }}
            minDate={HEATMAP_FIRST_AVAILABLE_DATE}
            maxDate={maxQueryDate}
            shouldDisableDate={(d) => !isMarketOpenOnDate(d)}
            format="YYYY-MM-DD"
            slotProps={{
              ...defaultSlotProps,
              textField: {
                ...defaultSlotProps.textField,
                style: {
                  width: isMobile ? '100px' : '135px',
                  fontSize: '14px',
                  marginBottom: '12px',
                  marginRight: '5px',
                },
              },
            }}
          />
        </LocalizationProvider>
      </Box>
    </SGTooltip>
  );

  const handlePriceBoundsChange = (
    _evt: Event,
    idx: number | number[],
    _activeThumb: number,
  ) => {
    if (!Array.isArray(idx)) {
      updateConfig({ priceZoom: PRICE_BOUNDS[idx] });
    }
  };

  const handleStrikeBoundsChange = (
    _evt: Event,
    idx: number | number[],
    _activeThumb: number,
  ) => {
    if (!Array.isArray(idx)) {
      updateConfig({ strikeZoom: STRIKE_ZOOM_BOUNDS[idx] });
    }
  };

  const volGauge = (
    <TraceVolGauge
      statsData={statsData}
      gammaAtLastPriceUninverted={gammaAtLastPriceUninverted}
      parquetKey={parquetKeys[0]}
      width={80}
      height={50}
      fontSize={12}
      suppress={suppressStability}
    />
  );

  const generalSettingsPopout = (
    <Box sx={{ height: '40px', alignSelf: 'center' }}>
      <SettingsPopout
        title="TRACE Chart Settings"
        popperID={'trace-chart-settings'}
        zIndex={1}
        placement="left-end"
      >
        <Box marginY="5px">
          <FormControl>
            <SGTooltip
              title="Select the color scale range for the Heatmap. Select ‘auto’ to dynamically adjust the coloration, or select another option for the desired level of contrast"
              placement="left"
            >
              <Typography variant="h4" gutterBottom color="primary">
                Scale Range
              </Typography>
            </SGTooltip>

            <RadioGroup
              row
              value={scaleRange}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                updateConfig({
                  scaleRange: event.target.value as OIScaleRange,
                })
              }
            >
              <FormControlLabel
                value={OIScaleRange.AUTO}
                control={<Radio />}
                label="auto"
              />
              <FormControlLabel
                value={OIScaleRange.LOW}
                control={<Radio />}
                label="low"
              />
              <FormControlLabel
                value={OIScaleRange.MEDIUM}
                control={<Radio />}
                label="medium"
              />
              <FormControlLabel
                value={OIScaleRange.HIGH}
                control={<Radio />}
                label="high"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box marginY="5px">
          <FormControl>
            <SGTooltip
              title="Select which HIRO symbol to plot on the HIRO line in the chart. S&P Equities sums the HIRO signal for all single stock components of the S&P 500, while S&P 500 combines the HIRO signal for SPX, XSP, SPY, & ES=F"
              placement="left"
            >
              <Typography variant="h4" gutterBottom color="primary">
                HIRO Symbol
              </Typography>
            </SGTooltip>

            <RadioGroup
              row
              value={hiroSym}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                updateConfig({
                  hiroSyms: {
                    ...(hiroSymsMap ?? {}),
                    [intradaySym]: event.target.value,
                  },
                })
              }
            >
              <SGTooltip title={'Not available premarket'} placement="left">
                <FormControlLabel
                  value={ComboSymbol.SPX_EQUITIES}
                  control={<Radio />}
                  label={ComboSymbol.SPX_EQUITIES}
                />
              </SGTooltip>
              <FormControlLabel
                value={ComboSymbol.SPX}
                control={<Radio />}
                label={ComboSymbol.SPX}
              />
              <FormControlLabel value={''} control={<Radio />} label="None" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Stack direction="row" justifyContent="center">
          <SGTooltip
            title={
              showColorScale
                ? 'Turn off the color scale on the right side'
                : 'Turn on the color scale on the right side'
            }
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showColorScale}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateConfig({
                      showColorScale: event.target.checked,
                    })
                  }
                />
              }
              label={<Typography>Color scale</Typography>}
              labelPlacement="end"
            />
          </SGTooltip>
          <SGTooltip
            title={
              showContourLines
                ? 'Turn off the contour lines on the heatmap'
                : 'Turn on the contour lines on the heatmap'
            }
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showContourLines}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateConfig({
                      showContourLines: event.target.checked,
                    })
                  }
                />
              }
              label={<Typography>Contour lines</Typography>}
              labelPlacement="end"
            />
          </SGTooltip>
          <SGTooltip
            title={
              showPriceLine
                ? 'Turn off the Price Action line overlay on the chart'
                : 'Turn on the Price Action line to see price movement relative to GEX levels'
            }
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showPriceLine}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateConfig({
                      showPriceLine: event.target.checked,
                    })
                  }
                />
              }
              label={<Typography>PA Line</Typography>}
              labelPlacement="end"
            />
          </SGTooltip>
        </Stack>

        <Stack gap={3} marginTop="10px" alignItems="center">
          <SGTooltip title="Select a 30, 60, or 90-day look back period for percentile stats, which are shown when hovering over the Strike Plot or Heatmap">
            <Typography variant="h4" gutterBottom color="primary">
              Statistics Look Back Period
            </Typography>
          </SGTooltip>
          <ButtonGroup
            aria-label="stats lookback settings"
            sx={{ width: '100%' }}
          >
            {[30, 60, 90].map((period) => (
              <Button
                size="small"
                key={period}
                variant={
                  period === statsLookbackDays ? 'contained' : 'outlined'
                }
                sx={{
                  fontSize: isMobile ? 12 : 14,
                  textTransform: 'none',
                  width: '33.3%',
                }}
                onClick={() => updateConfig({ statsLookbackDays: period })}
              >
                {period} days
              </Button>
            ))}
          </ButtonGroup>
        </Stack>

        <Stack direction="row" justifyContent="center" marginTop="10px">
          {theme.colorMode !== ColorMode.LIGHT && (
            <SGTooltip title="Enable/disable light mode for the TRACE heatmap; disabling this toggle will display the heatmap in dark mode">
              <FormControlLabel
                control={
                  <Switch
                    checked={useWhiteMode}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      updateConfig({
                        useWhiteMode: event.target.checked,
                      })
                    }
                  />
                }
                label={<Typography>Light Chart</Typography>}
              />
            </SGTooltip>
          )}
          <SGTooltip title="Enable three lines for each bar on the Strike Plot indicating the position by strike from 10, 30, and 60 minutes prior">
            <FormControlLabel
              control={
                <Switch
                  checked={trackerEnabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateConfig({
                      strikeBarsTrackerEnabled: event.target.checked,
                    })
                  }
                />
              }
              label={<Typography>Strike Plot Tracker</Typography>}
            />
          </SGTooltip>
          <SGTooltip title="Show the daily min/max range shadow (gray whiskers) on the Strike Plot. When disabled, the chart scales to the current bar values only.">
            <FormControlLabel
              control={
                <Switch
                  checked={historyEnabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateConfig({
                      strikeBarsHistoryEnabled: event.target.checked,
                    })
                  }
                />
              }
              label={<Typography>Strike Plot History</Typography>}
            />
          </SGTooltip>
        </Stack>
      </SettingsPopout>
    </Box>
  );

  const strikeZoomSlider = (
    <Box
      className="canvas-no-drag"
      width={isMobile ? '80px' : '100px'}
      sx={{ paddingTop: '10px' }}
      key="strikeZoomSlider"
    >
      <Slider
        aria-label="Zoom"
        valueLabelDisplay="auto"
        marks
        value={STRIKE_ZOOM_BOUNDS.indexOf(strikeZoomBounds)}
        min={0}
        max={STRIKE_ZOOM_BOUNDS.length - 1}
        onChange={handleStrikeBoundsChange}
      />
      <SGTooltip title="Slide right to zoom in further on strike plot X axis, or left to zoom out">
        <Typography
          sx={{
            fontSize: 12,
            width: 1,
            textAlign: 'center',
            display: 'inline-block',
          }}
        >
          Strike Plot Zoom
        </Typography>
      </SGTooltip>
    </Box>
  );

  const zoomSlider = (
    <Box
      width={isMobile ? '80px' : '100px'}
      sx={{ paddingTop: '10px' }}
      key="zoomSlider"
      className="canvas-no-drag"
    >
      <Slider
        aria-label="Zoom"
        valueLabelDisplay="auto"
        marks
        value={PRICE_BOUNDS.indexOf(priceBounds)}
        min={0}
        max={PRICE_BOUNDS.length - 1}
        onChange={handlePriceBoundsChange}
        disabled={zoomData != null} // if zoomed in via plotly, the zoom slider does nothing
      />
      <SGTooltip title="Slide right to zoom in further on the heatmap, or left to zoom out">
        <Typography
          sx={{
            fontSize: 12,
            width: 1,
            textAlign: 'center',
            display: 'inline-block',
          }}
        >
          Heatmap Zoom
        </Typography>
      </SGTooltip>
    </Box>
  );

  const strikeBarSelectors = (
    <Stack
      direction="row"
      gap={horizGap}
      key="strikeBarSelectors"
      alignItems="center"
    >
      {!isMobile && (
        <ChartSizingButton
          onClick={() => {
            setShowChartType(
              showChartType === TraceShowChartType.StrikeBars
                ? TraceShowChartType.Both
                : TraceShowChartType.StrikeBars,
            );
          }}
          icon={
            showChartType === TraceShowChartType.StrikeBars ? (
              <FullscreenExitSharpIcon />
            ) : (
              <FullscreenSharpIcon />
            )
          }
          title={
            showChartType === TraceShowChartType.StrikeBars
              ? 'Minimize the Strike Plot by showing the Heatmap and color scale'
              : 'Maximize the Strike Plot by hiding the Heatmap and color scale'
          }
          buttonSx={{ height: '40px', marginTop: '-1px' }}
        />
      )}
      <SGSelect
        key={'strike_bar_type'}
        label={'Strike Bar Type'}
        multiple={false}
        value={strikeBarType}
        setter={(val) => handleStrikeBarTypeChange(val)}
        options={[
          TraceStrikeBarType.GAMMA,
          TraceStrikeBarType.OI,
          TraceStrikeBarType.OI_NET,
          TraceStrikeBarType.NONE,
        ]}
        optionsTextTransform={(s) => StrikeBarTypeLabels.get(s) ?? s}
        tooltipTransform={(s) => StrikeBarTypeTooltips.get(s)!}
        selectTooltipProps={{
          title:
            'Select from GEX, OI, or Net OI to display on the Strike Plot, or hide the chart by selecting None',
        }}
        sx={{ height: '40px' }}
      />
      <Box
        visibility={
          strikeBarType === TraceStrikeBarType.NONE ? 'hidden' : 'visible'
        }
        sx={{
          marginTop: isMobile ? undefined : '-4px',
        }}
        className="canvas-no-drag"
      >
        <SGTooltip
          title={`Show only 0dte ${StrikeBarTypeLabels.get(
            strikeBarType,
          )!} in the strike bar chart on the left. Note that these values will be 0 after market close.`}
        >
          <FormControlLabel
            control={
              <Switch
                checked={showGexZeroDte}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  updateConfig({
                    showGexZeroDte: event.target.checked,
                  })
                }
              />
            }
            label={
              <Typography fontSize="12px">
                0DTE {StrikeBarTypeLabels.get(strikeBarType)!}
              </Typography>
            }
            labelPlacement={'bottom'}
            sx={{ marginX: 0 }}
          />
        </SGTooltip>
        {!isMobile && (
          <InfoButton
            articleKey={`TRACE-STRIKE-PLOT`}
            tooltipTitle="Learn more about the TRACE Strike Plot"
          />
        )}
      </Box>
      {strikeZoomSlider}
    </Stack>
  );

  const keyLevelsSwitch = (
    <Box
      sx={{
        marginTop: '-4px',
        paddingTop: replaceTitleWithTools ? undefined : '10px',
      }}
      key="keyLevelsSwitch"
      className="canvas-no-drag"
    >
      <SGTooltip title="Overlay SpotGamma’s key levels for SPX, including the Call Wall, Put Wall, Hedge Wall, Key Gamma Strike, and SG Implied 1-Day Move">
        <FormControlLabel
          control={
            <Switch
              checked={showKeyLevels}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                updateConfig({
                  showKeyLevels: event.target.checked,
                })
              }
            />
          }
          label={
            <Typography textAlign="center" fontSize="12px">
              Key levels
            </Typography>
          }
          labelPlacement={'bottom'}
          sx={{ marginX: 0, alignSelf: 'center' }}
        />
      </SGTooltip>
    </Box>
  );

  const zoomOutButton = (
    <Box
      key="zoomOutButton"
      visibility={zoomData == null ? 'hidden' : 'visible'}
      sx={{
        alignSelf: 'center',
      }}
    >
      <Button
        sx={{ fontSize: '12px', textTransform: 'none' }}
        onClick={resetZoom}
        variant="text"
      >
        <ZoomOutIcon />
        &nbsp; Zoom Out
      </Button>
    </Box>
  );

  const candleDurationSelect = (
    <SGSelect
      key={'candle_duration'}
      label={'Candle Duration'}
      multiple={false}
      value={candleDuration}
      setter={(newVal: number) =>
        updateConfig({
          candleDuration: newVal,
        })
      }
      options={[60, 5 * 60, 15 * 60]}
      optionsTextTransform={(duration: any) => `${duration / 60}m`}
      tooltipTransform={(duration: any) =>
        `Set the duration of each price candle to ${duration / 60} minute${
          duration / 60 > 1 ? 's' : ''
        }`
      }
      sx={{
        width: '75px',
        height: '40px',
        textAlign: 'center',
        alignSelf: 'center',
      }}
      selectTooltipProps={{
        placement: 'bottom',
        title:
          'Customize the price candles on the heatmap to intervals of 1 minute, 5 minutes, or 15 minutes',
      }}
    />
  );

  const lenseSelect = (
    <Stack direction="row">
      <SGSelect
        key="intraday-gamma-filters"
        label="Lenses"
        multiple={false}
        value={selectedLense}
        setter={(newVal: any) => {
          updateConfig({ lense: newVal });
        }}
        options={
          showInternalLenses
            ? Object.values(TraceLense)
                .filter((k) => typeof k != 'string')
                .sort((a: any, b: any) => a - b)
            : TRACE_LENSES
        }
        optionsTextTransform={(filter: any) =>
          TraceFiltersReadable.get(filter) ?? ''
        }
        tooltipTransform={(filter: any) => IntradayFiltersTooltip.get(filter)}
        sx={{
          width: '125px',
          height: '40px',
          textAlign: 'center',
          alignSelf: 'center',
        }}
        selectTooltipProps={{
          placement: 'left',
          title:
            'Select from three models available for TRACE: Gamma, Delta Pressure, and Charm Pressure. Click on the adjacent (i) Info icon to learn more about each model',
        }}
      />
      {!isMobile && (
        <InfoButton
          articleKey={`TRACE-${TraceLense[selectedLense]}`}
          tooltipTitle="Learn more about the current model displayed on the heatmap"
        />
      )}
    </Stack>
  );

  const timeCutoff = (
    <Box
      visibility={isLatestTimestamp ? 'hidden' : 'visible'}
      sx={{ alignSelf: 'center' }}
      className="canvas-no-drag"
    >
      <SGTooltip
        title={
          'Limit the price candles and HIRO datapoints shown to only those up to your selected timestamp'
        }
        placement="bottom-start"
      >
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={filterPrice}
              onChange={(evt) => {
                setFilterPrice(evt.target.checked);
              }}
              size="small"
            />
          }
          label={
            <Typography
              sx={{
                fontSize: 12,
                color: theme.palette.primary.main,
              }}
            >
              Time Cutoff
            </Typography>
          }
        />
      </SGTooltip>
    </Box>
  );

  const strikeBarInToolbar =
    !isMobile || showChartType === TraceShowChartType.StrikeBars;
  const heatmapInToolbar =
    !isMobile || showChartType === TraceShowChartType.Heatmap;

  const toolbarComponents = [
    [heatmapInToolbar && !replaceTitleWithTools, keyLevelsSwitch],
    [heatmapInToolbar && !replaceTitleWithTools, candleDurationSelect],
    [heatmapInToolbar, zoomSlider],
  ];

  const componentsInToolbar = toolbarComponents.map((arr) => arr[0] && arr[1]);

  const marketParticipantSelect = userTraceV2Enabled && (
    <SGSelect
      label="Market Participant"
      multiple={false}
      value={parquetKeys?.[0] || OIEntity.MARKET_MAKER}
      setter={(newVal: any) => setParquetKeys([newVal])}
      options={OIENTITY_READABLE_ARRAY.map(([value, _label]) => value)}
      optionsTextTransform={(o) => OIENTITY_READABLE.get(o)}
      valueTextTransform={(o) => OIENTITY_READABLE.get(o)}
      menuItemStyleTransform={(o) => {
        if (o === OIEntity.MARKET_MAKER) {
          return {
            textDecoration: 'underline!important',
          };
        }
        return undefined;
      }}
      sx={{
        height: '40px',
        textAlign: 'center',
        alignSelf: 'center',
      }}
      selectTooltipProps={{
        placement: 'left',
        title:
          'Select the market participant you would like to see power TRACE. TRACE uses Market Maker as the default selection.',
      }}
      tooltipTransform={(mktActor) => OIENTITY_TOOLTIP.get(mktActor)}
    />
  );

  const invertedSwitch = userTraceV2Enabled && (
    <Box
      className="canvas-no-drag"
      sx={{
        marginTop: '-4px',
        paddingTop: isMobile
          ? undefined
          : replaceTitleWithTools
            ? undefined
            : '10px',
      }}
      key="keyLevelsSwitch"
    >
      <SGTooltip title="Invert the heatmap and GEX bars for the selected market participant (this makes negative values positive, and positive values negative).">
        <FormControlLabel
          control={
            <Switch
              checked={isInverted}
              onChange={() => setIsInverted(!isInverted)}
            />
          }
          label={
            <Typography textAlign="center" fontSize="12px">
              Inverted
            </Typography>
          }
          labelPlacement={'bottom'}
          sx={{ marginX: 0, alignSelf: 'center' }}
        />
      </SGTooltip>
    </Box>
  );

  const actionButtons = (
    <Stack direction="row" alignItems="center">
      {/* Canvas tiles have their own expand button — don't duplicate it. */}
      {mode === 'standalone' && (
        <ChartSizingButton
          onClick={() => setFullscreen(!fullscreen)}
          icon={
            fullscreen ? <FullscreenExitSharpIcon /> : <FullscreenSharpIcon />
          }
          title={fullscreen ? 'Minimize TRACE' : 'Maximize TRACE'}
          tooltipPlacement="bottom-start"
          buttonSx={{ height: '40px', marginTop: '-1px' }}
        />
      )}
      {generalSettingsPopout}
      <TraceColorSettings />
    </Stack>
  );

  const toolbar = isMobile ? (
    <Stack direction="column" gap="6px" marginTop="3px" alignItems="center">
      <Stack
        direction="row"
        gap="8px"
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        rowGap="4px"
      >
        {marketParticipantSelect}
        {invertedSwitch}
        {componentsInToolbar}
        {actionButtons}
      </Stack>
      {strikeBarInToolbar && (
        <Stack
          direction="row"
          gap="8px"
          alignItems="center"
          justifyContent="center"
          flexWrap="wrap"
          rowGap="4px"
        >
          {strikeBarSelectors}
        </Stack>
      )}
    </Stack>
  ) : (
    <Stack
      direction="row"
      gap={horizGap}
      marginTop="3px"
      justifyContent="space-between"
      alignItems="center"
    >
      {strikeBarInToolbar && strikeBarSelectors}
      <Stack direction="row" gap={horizGap} alignItems="center">
        {marketParticipantSelect}
        {invertedSwitch}
        {componentsInToolbar}
        {actionButtons}
      </Stack>
    </Stack>
  );

  const showClosedMsg =
    isLatestTradingDay(getCurrentDate()) &&
    getCurrentDate().hour() >= LAST_TRACE_UPDATE_HOUR_ET;

  const showHeaderText = !replaceTitleWithTools && !isMobile;
  const header = (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      justifyContent={isMobile ? 'center' : 'space-between'}
      alignItems="center"
      marginRight={fullscreen ? '20px' : 0}
      marginTop={fullscreen ? '10px' : 0}
      sx={{ background: theme.palette.background.default }}
      gap={isMobile ? '8px' : '20px'}
    >
      <Stack direction="column" gap="2px">
        {showHeaderText && (
          <Typography fontSize={16} textAlign="left">
            {intradaySym} {TraceFiltersReadable.get(selectedLense)} Exposure
            {TRACE_BAR_TITLES.get(strikeBarType)
              ? ` and ${TRACE_BAR_TITLES.get(strikeBarType)}`
              : ''}{' '}
            - {OIENTITY_READABLE.get(parquetKeys?.[0] ?? OIEntity.MARKET_MAKER)}{' '}
            {isInverted ? '(Inverted)' : ''}
          </Typography>
        )}
        {showHeaderText && lastUpdateCheckAt != null && !showClosedMsg && (
          <SGTooltip
            title={
              'To ensure you always have the latest data, TRACE checks for updates every few seconds, but there will usually only be a new update every 10-15 minutes. Updates will not necessarily occur at exact 10 minute intervals'
            }
            placement="left"
          >
            <Typography
              color={theme.palette.trace.updateStatusColor}
              sx={{ fontSize: '11px', textAlign: 'left' }}
            >
              Last checked for update at{' '}
              {lastUpdateCheckAt.tz(tz).format('HH:mm')}
            </Typography>
          </SGTooltip>
        )}
        {showHeaderText && showClosedMsg && (
          <Typography
            color={theme.palette.trace.updateStatusColor}
            sx={{ fontSize: '11px', textAlign: 'left' }}
          >
            TRACE does not update after{' '}
            {getCurrentDate()
              .hour(LAST_TRACE_UPDATE_HOUR_ET)
              .tz(tz)
              .format('HH:00')}
          </Typography>
        )}
      </Stack>

      <Stack direction="row" gap={horizGap}>
        {replaceTitleWithTools && heatmapInToolbar && keyLevelsSwitch}
        {replaceTitleWithTools && heatmapInToolbar && candleDurationSelect}
        {volGauge}
        {lenseSelect}
        {datePicker}
      </Stack>
    </Stack>
  );

  return (
    <Stack
      sx={{
        marginBottom: '15px',
        paddingX: isMobile ? 0 : '10px',
        margin: 'auto',
        width: 1,
      }}
      gap="0px"
    >
      {fullscreen && <HideSupportStyle />}
      <Box width={1} flexGrow={1} textAlign="center">
        {header}
      </Box>
      {toolbar}
      <Stack
        direction="row-reverse"
        gap="10px"
        height="23px"
        marginBottom="3px"
      >
        {zoomOutButton}
        {timeCutoff}
      </Stack>
    </Stack>
  );
};
